import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../api';
import { sendAttributionOnce } from '../utils/attribution';
import { redeemInviteOnce } from '../utils/invite';
import { COUNTS_CHANGED_EVENT } from '../utils/counts';
import { THEME_OPTIONS, applyTheme, clearTheme, getThemePreference, saveThemePreference } from '../utils/theme';
import SvodxLogo from './brand/SvodxLogo';
import './AppShell.css';

const TIER_NAMES = { free: 'Бесплатный', advanced: 'Расширенный', corporate: 'Корпоративный' };
// null — без ограничений
const POLL_LIMITS = { free: 2, advanced: 15, corporate: null };
const ACTIVE_LIMITS = { free: 1, advanced: 5, corporate: 25 };
const PAID_TIERS = ['advanced', 'corporate'];

const Icon = ({ name }) => {
    const paths = {
        polls: <path d="M4 20V10M10 20V4M16 20v-7M21 20H3" />,
        qa: <><path d="M21 12a8 8 0 01-11.6 7.1L4 20l1-4.6A8 8 0 1121 12z" /><path d="M10 9.5a2 2 0 113 1.7c-.6.4-1 .8-1 1.5M12 15h.01" /></>,
        file: <><path d="M14 3H7a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V8z" /><path d="M14 3v5h5M9 13h6M9 17h4" /></>,
        tour: <><path d="M22 10L12 5 2 10l10 5 10-5z" /><path d="M6 12v5c3 2 9 2 12 0v-5" /></>,
        help: <><circle cx="12" cy="12" r="9" /><path d="M9.5 9a2.5 2.5 0 114 2c-.9.6-1.5 1-1.5 2M12 17h.01" /></>,
        profile: <><circle cx="12" cy="8" r="4" /><path d="M4 21c1.5-4 4.5-6 8-6s6.5 2 8 6" /></>,
        admin: <path d="M12 2l8 3v6c0 5-3.4 9.7-8 11-4.6-1.3-8-6-8-11V5z" />,
        logout: <path d="M15 4h4v16h-4M10 8l-4 4 4 4M6 12h11" />,
        plus: <path d="M12 5v14M5 12h14" />,
        menu: <path d="M4 7h16M4 12h16M4 17h16" />,
        close: <path d="M6 6l12 12M18 6L6 18" />,
    };
    return (
        <svg className="shell-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            {paths[name]}
        </svg>
    );
};

function initials(user) {
    const name = (user?.first_name || user?.username || user?.email || '?').trim();
    return name.charAt(0).toUpperCase();
}

/**
 * Каркас личного кабинета: боковое меню, верхняя панель, на телефоне — выезжающее меню.
 * Действия (создать опрос, обучение, фильтр) передаёт страница;
 * если страница их не передала, переходим в кабинет с параметром, и он выполнит действие сам.
 */
const AppShell = ({
    user: userProp,
    counts: countsProp,
    section,
    title,
    onCreatePoll,
    onShowPolls,
    onStartTour,
    children,
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [loadedUser, setLoadedUser] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [inviteNotice, setInviteNotice] = useState(null);
    const [themePref, setThemePref] = useState(getThemePreference);
    const menuRef = useRef(null);

    const user = userProp || loadedUser;
    const counts = countsProp || (loadedUser
        ? { total: loadedUser.poll_count || 0, active: loadedUser.active_poll_count || 0, archived: loadedUser.archived_poll_count || 0 }
        : null);
    // Счётчики «Вопросов из зала»: всего сессий и сколько принимают вопросы сейчас
    const [qaFresh, setQaFresh] = useState(null);
    const qaCounts = qaFresh || (user ? { total: user.qa_session_count || 0, open: user.qa_open_count || 0 } : null);

    // Сессию создали, удалили, открыли или закрыли приём — перечитываем счётчики
    useEffect(() => {
        const reload = () => {
            api.get('/users/me/profile')
                .then((res) => setQaFresh({ total: res.data.qa_session_count || 0, open: res.data.qa_open_count || 0 }))
                .catch(() => {});
        };
        window.addEventListener(COUNTS_CHANGED_EVENT, reload);
        return () => window.removeEventListener(COUNTS_CHANGED_EVENT, reload);
    }, []);

    // Источник регистрации (UTM, реферальная ссылка) уходит на сервер один раз после входа
    useEffect(() => {
        if (user?.id) sendAttributionOnce(api);
    }, [user?.id]);

    // Приглашение из ссылки применяется после входа; тариф обновится при следующей загрузке страницы
    useEffect(() => {
        if (!user?.id) return;
        let cancelled = false;
        redeemInviteOnce(api).then((result) => {
            if (cancelled || !result) return;
            if (result.ok && result.status === 'accepted') {
                const until = result.expires_at ? ` до ${new Date(result.expires_at).toLocaleDateString('ru-RU')}` : '';
                setInviteNotice({
                    type: 'success',
                    text: result.tier && result.tier !== 'free'
                        ? `Приглашение принято: подключён тариф ${result.gift}${until}.`
                        : 'Приглашение принято. Добро пожаловать в СВОД!',
                    reload: Boolean(result.tier && result.tier !== 'free'),
                });
            } else if (!result.ok) {
                setInviteNotice({ type: 'error', text: result.error });
            }
        });
        return () => { cancelled = true; };
    }, [user?.id]);

    // Страница не передала пользователя (например, справка) — загружаем сами
    useEffect(() => {
        if (userProp) return;
        api.get('/users/me/profile')
            .then((res) => setLoadedUser(res.data))
            .catch((err) => {
                if (err.response?.status === 401) navigate('/login');
            });
    }, [userProp, navigate]);

    // Тема действует только в кабинете: при уходе на публичную страницу возвращаем светлую
    useEffect(() => {
        applyTheme(themePref);
        if (themePref !== 'system' || !window.matchMedia) return undefined;
        const media = window.matchMedia('(prefers-color-scheme: dark)');
        const onChange = () => applyTheme('system');
        media.addEventListener?.('change', onChange);
        return () => media.removeEventListener?.('change', onChange);
    }, [themePref]);

    useEffect(() => () => clearTheme(), []);

    const chooseTheme = (value) => {
        setThemePref(value);
        saveThemePreference(value);
    };

    // Меню закрывается при переходе на другую страницу
    useEffect(() => {
        setDrawerOpen(false);
        setMenuOpen(false);
    }, [location.pathname, location.search]);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape') {
                setDrawerOpen(false);
                setMenuOpen(false);
            }
        };
        const onClick = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
        };
        document.addEventListener('keydown', onKey);
        document.addEventListener('mousedown', onClick);
        return () => {
            document.removeEventListener('keydown', onKey);
            document.removeEventListener('mousedown', onClick);
        };
    }, []);

    useEffect(() => {
        document.body.classList.toggle('shell-drawer-open', drawerOpen);
        return () => document.body.classList.remove('shell-drawer-open');
    }, [drawerOpen]);

    const run = (action, fallbackUrl) => {
        setDrawerOpen(false);
        setMenuOpen(false);
        if (action) action();
        else navigate(fallbackUrl);
    };

    const logout = () => {
        localStorage.removeItem('token');
        navigate('/login');
    };

    const tier = user?.subscription_tier || 'free';
    // null у корпоративного — «без ограничений»; неизвестный тариф считаем бесплатным
    const knownTier = tier in POLL_LIMITS ? tier : 'free';
    const pollMax = POLL_LIMITS[knownTier];
    const activeMax = ACTIVE_LIMITS[knownTier];
    const total = counts?.total ?? 0;
    const active = counts?.active ?? 0;
    const archived = counts?.archived ?? 0;
    // На бесплатном тарифе архивные опросы занимают места в лимите, на платных — нет
    const usedSlots = knownTier === 'free' ? total + archived : total;
    const canCreate = !user || user.status === 'active';
    const displayName = user?.first_name || user?.username || user?.email || 'Профиль';

    const navItem = (key, icon, label, onClick, extra = null, attrs = {}) => (
        <button
            type="button"
            className={`shell-nav__item${section === key ? ' is-active' : ''}`}
            onClick={onClick}
            aria-current={section === key ? 'page' : undefined}
            {...attrs}
        >
            <Icon name={icon} />
            <span className="shell-nav__label">{label}</span>
            {extra}
        </button>
    );

    return (
        <div className={`shell${drawerOpen ? ' shell--drawer-open' : ''}`}>
            <div className="shell__scrim" onClick={() => setDrawerOpen(false)} aria-hidden />

            <aside className="shell-side" aria-label="Меню кабинета">
                <div className="shell-side__head">
                    <button type="button" className="shell-logo" onClick={() => run(onShowPolls && (() => onShowPolls('all')), '/dashboard')}>
                        <span className="shell-logo__text">
                            <SvodxLogo className="shell-logo__word" />
                            <span>Сервис Визуализации Опросов и Данных</span>
                        </span>
                    </button>
                    <button type="button" className="shell-icon-btn shell-side__close" onClick={() => setDrawerOpen(false)} aria-label="Закрыть меню">
                        <Icon name="close" />
                    </button>
                </div>

                {canCreate && (
                    <button
                        type="button"
                        className="shell-create"
                        onClick={() => run(onCreatePoll, '/dashboard?create=1')}
                        data-tour="create-poll"
                    >
                        <Icon name="plus" /> Создать опрос
                    </button>
                )}

                <nav className="shell-nav">
                    <div className="shell-nav__section">Инструменты</div>
                    {navItem('polls', 'polls', 'Мои опросы',
                        () => run(onShowPolls && (() => onShowPolls('all')), '/dashboard'),
                        counts && <span className="shell-nav__count">{total}</span>)}
                    {navItem('qa', 'qa', 'Вопросы из зала', () => run(null, '/qa'),
                        user && (!PAID_TIERS.includes(tier)
                            ? <span className="shell-nav__pro" title="На расширенном и корпоративном тарифах">PRO</span>
                            : qaCounts && qaCounts.total > 0 && (
                                <span className="shell-nav__count shell-nav__count--qa" title={`Сессий: ${qaCounts.total}, приём открыт: ${qaCounts.open}`}>
                                    {qaCounts.total}
                                    {qaCounts.open > 0 && <span className="shell-nav__live"><i aria-hidden />{qaCounts.open}</span>}
                                </span>
                            )),
                        { 'data-tour': 'qa-link' })}
                    {navItem('presentations', 'file', 'Ваши презентации', () => run(null, '/my-presentations'),
                        user && !PAID_TIERS.includes(tier) && <span className="shell-nav__pro" title="На расширенном и корпоративном тарифах">PRO</span>,
                        { 'data-tour': 'presentations-link' })}

                    <div className="shell-nav__section">Помощь</div>
                    {navItem('tour', 'tour', 'Обучение', () => run(onStartTour, '/dashboard?tour=1'), null, { 'data-tour': 'tour-link' })}
                    {navItem('help', 'help', 'Справка', () => run(null, '/help'), null, { 'data-tour': 'help-link' })}

                    <div className="shell-nav__section">Аккаунт</div>
                    {navItem('profile', 'profile', 'Профиль и тариф', () => run(null, '/profile'), null, { 'data-tour': 'profile-link' })}
                    {user?.role === 'admin' && navItem('admin', 'admin', 'Админ-панель', () => run(null, '/admin'))}
                </nav>

                {/* На странице профиля тариф и лимиты показаны подробно — карточка не нужна */}
                {user && section !== 'profile' && (
                    <div className="shell-plan">
                        <div className="shell-plan__head">
                            <span>Тариф</span>
                            <span className={`shell-plan__badge shell-plan__badge--${tier}`}>{TIER_NAMES[tier] || tier}</span>
                        </div>
                        <div className="shell-plan__meter">
                            <span>
                                Опросов: {pollMax === null ? `${total} · без ограничений` : `${usedSlots} из ${pollMax}`}
                                {archived > 0 && <> · в архиве {archived}</>}
                            </span>
                            {pollMax !== null && (
                                <div className="shell-plan__bar"><i style={{ width: `${Math.min(100, (usedSlots / pollMax) * 100)}%` }} /></div>
                            )}
                        </div>
                        <div className="shell-plan__meter">
                            <span>Активных: {active} из {activeMax}</span>
                            <div className="shell-plan__bar"><i style={{ width: `${Math.min(100, (active / activeMax) * 100)}%` }} /></div>
                        </div>
                        <button type="button" className="shell-plan__link" onClick={() => run(null, '/profile')}>
                            {tier === 'free' ? 'Улучшить тариф →' : 'Тариф и оплата →'}
                        </button>
                    </div>
                )}
            </aside>

            <div className="shell-body">
                <header className="shell-top">
                    <button type="button" className="shell-icon-btn shell-top__burger" onClick={() => setDrawerOpen(true)} aria-label="Открыть меню" aria-expanded={drawerOpen}>
                        <Icon name="menu" />
                    </button>
                    <button type="button" className="shell-top__logo" onClick={() => navigate('/dashboard')}>
                        <SvodxLogo />
                    </button>
                    <div className="shell-top__crumbs">
                        Кабинет{title ? <> / <b>{title}</b></> : null}
                    </div>
                    <div className="shell-top__spacer" />
                    {/* На широком экране справка есть в боковом меню; кнопка нужна, когда меню скрыто */}
                    <button type="button" className="shell-icon-btn shell-top__help" onClick={() => navigate('/help')} title="Справка" aria-label="Справка" data-tour="help-link">
                        <Icon name="help" />
                    </button>
                    <div className="shell-user" ref={menuRef}>
                        <button
                            type="button"
                            className="shell-user__btn"
                            onClick={() => setMenuOpen((v) => !v)}
                            aria-haspopup="menu"
                            aria-expanded={menuOpen}
                            data-tour="profile-link"
                        >
                            <span className="shell-user__avatar">{initials(user)}</span>
                            <span className="shell-user__meta">
                                <b>{displayName}</b>
                                <small>{TIER_NAMES[tier] || tier}</small>
                            </span>
                            <span className="shell-user__caret" aria-hidden>▾</span>
                        </button>
                        {menuOpen && (
                            <div className="shell-user__menu" role="menu">
                                <div className="shell-user__menu-head">
                                    <b>{displayName}</b>
                                    {user?.email && <span>{user.email}</span>}
                                </div>
                                <button type="button" role="menuitem" onClick={() => run(null, '/profile')}>
                                    <Icon name="profile" /> Профиль и тариф
                                </button>
                                <div className="shell-theme">
                                    <span className="shell-theme__label" id="shell-theme-label">Оформление</span>
                                    <div className="shell-theme__options" role="radiogroup" aria-labelledby="shell-theme-label">
                                        {THEME_OPTIONS.map((o) => (
                                            <button
                                                key={o.value}
                                                type="button"
                                                role="radio"
                                                aria-checked={themePref === o.value}
                                                title={o.title}
                                                onClick={() => chooseTheme(o.value)}
                                            >
                                                {o.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div className="shell-user__sep" />
                                <button type="button" role="menuitem" className="shell-user__logout" onClick={logout}>
                                    <Icon name="logout" /> Выйти
                                </button>
                            </div>
                        )}
                    </div>
                </header>

                <main className="shell-main">
                    {inviteNotice && (
                        <div className={`shell-notice shell-notice--${inviteNotice.type}`} role="status">
                            <span>{inviteNotice.text}</span>
                            {inviteNotice.reload ? (
                                <button type="button" onClick={() => window.location.reload()}>Обновить</button>
                            ) : (
                                <button type="button" onClick={() => setInviteNotice(null)} aria-label="Скрыть">✕</button>
                            )}
                        </div>
                    )}
                    {children}
                </main>
            </div>
        </div>
    );
};

export default AppShell;
